import "./Blocks.scss";

import clsx from "clsx";
import { compact, get, throttle } from "lodash-es";
import { type FC, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import RenderIfVisible from "react-render-if-visible";
import type { Doc } from "sharedb/lib/client";

import {
  LINKED_SECTION_SEEDING_ACCESS_ID,
  canRollupAccessId,
  groupAccessIdFromGroupId,
} from "../../shared/access-id";
import type {
  BlockType,
  ChoiceFieldType,
  CodeFieldType,
  EmbedFieldType,
  FieldType,
  FileFieldType,
  PathType,
  WorksheetType,
} from "../../shared/types/worksheet";
import {
  BLOCKS_PATH,
  DATA_PATH,
  MAX_BLOCK_COUNT,
  getBlockIndexId,
  isQuestionBlock,
} from "../../shared/worksheets/constants";
import {
  deleteBlockFromWorksheet,
  hasEditableBlocks,
  instructorFieldIsInitialized,
} from "../../shared/worksheets/doc-helpers";
import {
  getMeasuredFieldFromBlock,
  getUserFieldProgress,
} from "../../shared/worksheets/progress-count";
import { BlockMoveButtons } from "../components/Block/BlockMoveButtons";
import { BlockToolbar } from "../components/Block/BlockToolbar";
import { Field } from "../components/Field";

import { ErrorBoundary } from "./ErrorBoundary";
import { TemplateBlockButton, getTemplateFieldIndex } from "./TemplateBlockButton";
import { VerticalSpacer } from "./VerticalSpacer";

import { PAGE_SCROLL_CONTAINER_ID } from "components/constants";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { isValidAccessIdForPageType } from "components/materials/page/groups/helpers";
import { FeatureButton } from "components/materials/presentation/featuring/FeatureButton";
import { t } from "i18n/i18n";
import { Card } from "mds/components/Card";
import { IconText } from "mds/components/IconText";
import { useHoverable } from "mds/hooks/use-hoverable";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { WhiteboardIcon } from "mds/icons";
import { DocViewContext } from "providers/DocViewProvider";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import { pageGroupSelectors, pageSelectors, selectCurrentSectionIsLinked } from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { toastLocalizedOperationError } from "utils/alerts";
import { updatePageStaticContentStatus } from "utils/page";
import { isGroupPage } from "utils/page-utils";
import { FeatureStatus, usePresentation } from "utils/presentation";
import { AddElement } from "worksheets/components/AddElement";
import {
  CodeCellTypeSwitchDropdown,
  MultipleSwitchDropdown,
} from "worksheets/components/Field/AuthoringFields";
import { BlockResponsesHeader } from "worksheets/components/Field/BlockResponsesHeader";
import { EditEmbedButton } from "worksheets/components/Field/Embed/EditEmbedButton";
import { blockHasDifferingSeedText } from "worksheets/field-versioning";

// Estimated are based on empty fields
const ESTIMATED_AVG_FIELD_HEIGHT_PX_MAP: Record<FieldType["t"], number> = {
  Text: 32,
  RichTextField: 130,
  ChoiceField: 90,
  FileField: 46,
  TableField: 160,
  EmbedField: 32,
  CodeField: 90,
  // AssetField doesn't exist empty. Estimating roughly for a big image or PDF.
  AssetField: 400,
  // This is equivalent to --page-default-max-width, because whiteboards are square and scale to that width (so also to that height)
  WhiteboardField: 660,
};

const FALLBACK_FIELD_HEIGHT_PX = 200;

interface BlockProps {
  accessIdPath: PathType;
  doc: Doc<WorksheetType>;
  blockPath: PathType;
  isFirstBlock: boolean;
  isLastBlock: boolean;
  blockIndex: number;
  onMove: (changeAmount: 1 | -1) => void;
  onDelete: () => void;
}

const Block: FC<BlockProps> = ({
  accessIdPath,
  blockPath,
  doc,
  onMove,
  onDelete,
  blockIndex,
  isFirstBlock,
  isLastBlock,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { isAuthoringPage, isFeaturing, accessId, pageId, isProjecting, showResponses } =
    useContext(DocViewContext);
  const { isPresenting, featureStatus, presentedBlockId, isFollowing } = usePresentation();
  const { canAuthorSection } = useCapabilities();
  const isLinkedSection = useAppSelector(selectCurrentSectionIsLinked);
  const isMobile = !useIsMdOrLarger();
  const block = doc.data.b[blockIndex] as BlockType | undefined;
  const notFeaturing = featureStatus === FeatureStatus.NONE;
  const blockIsFeatured = block?.id === presentedBlockId;
  const featuringABlock = presentedBlockId && (isFollowing || isPresenting || isProjecting);
  const showAddBlock = !isFirstBlock && !featuringABlock;
  const [blockHeight, setBlockHeight] = useState<number>(0);
  const page = useAppSelector((s) => pageSelectors.selectById(s, pageId));
  const pageGroups = useAppSelector((s) => pageGroupSelectors.filterByPageId(s, pageId));
  const pageGroupAccessIds = pageGroups.map((group) => groupAccessIdFromGroupId(group.id));
  const isMdOrLarger = useIsMdOrLarger();

  // Need to keep track of this as hiding it after it was featured makes closing the modal
  // act like it was closed manually
  const [wasFeatured, setWasFeatured] = useState<boolean>(false);
  const canFeature =
    isMdOrLarger &&
    isPresenting &&
    !isProjecting &&
    [
      FeatureStatus.CLASS_RESPONSES,
      FeatureStatus.NONE,
      FeatureStatus.PARTICIPANT_RESPONSE,
    ].includes(featureStatus) &&
    // Since we can't display all whiteboards when featuring all class responses, we don't show the feature button for whiteboard blocks on that page.
    !(block?.t === "Whiteboard" && !accessId);

  useEffect(() => {
    if (blockIsFeatured) {
      setWasFeatured(true);
    }
  }, [blockIsFeatured]);

  useLayoutEffect(() => {
    if (ref.current) {
      setBlockHeight(ref.current.clientHeight);
    }
  }, []);

  const blockHover = useHoverable();

  const canModifyBlocks = isAuthoringPage && !showResponses && !isMobile && notFeaturing;

  // Note: If the amount of fields changes, this will not be updated, but
  // we currently only support a fixed set of fields based on block type, so it's fine.
  const fieldPaths = useMemo(
    () => (block?.f ? block.f.map((bc, index) => [...blockPath, "f", String(index)]) : []),
    [block?.f, blockPath],
  );

  const templateFieldIndex = getTemplateFieldIndex({
    accessId,
    block,
    canViewTemplateModal: !canAuthorSection || isLinkedSection,
    showResponses,
  });

  if (!block || !page) {
    return null;
  }

  const hasSwitchForMultiple = block.t === "MultipleChoice" || block.t === "File";
  // By convention, all blocks only have at most one collaborative field, and if there is one, then it's
  // the latter of max two fields. This _might_ change, see `DEFAULT_BLOCKS` in `constants.ts`.
  const collaborativeField = block.f[block.f.length - 1];
  const isCodeBlock = block.t === "Code";
  const isEmbedField = block.t === "Embed";
  const isWhiteboardBlock = block.t === "Whiteboard";
  const interactiveFieldIndex = block.f.findIndex((f) => f === collaborativeField);
  const interactiveFieldPath = fieldPaths[interactiveFieldIndex];

  const measuredField = getMeasuredFieldFromBlock(block);
  const allAccessIds = Object.keys(get(doc.data, DATA_PATH))
    .filter(canRollupAccessId)
    .filter((currAccessId) => {
      // Skip access ids of the wrong type (e.g., student access ids on a group page or vice-versa).
      if (!isValidAccessIdForPageType(page, currAccessId)) {
        return false;
      }
      // For group Pages, skip access ids that refer to groups that are not in the current PageGroup
      // data. If the Page was imported from another environment, compensate for missing PageGroups
      // by allowing this type of access id.
      if (isGroupPage(page) && !pageGroupAccessIds.includes(currAccessId) && !page?.is_imported) {
        return false;
      }
      return true;
    });

  const totalStudentsCount = showResponses === "ALL" ? allAccessIds.length : 0;
  const studentsMadeProgressCount =
    showResponses === "ALL" && measuredField
      ? allAccessIds.filter((aid) => getUserFieldProgress(doc, measuredField.id, aid).touched)
          .length
      : 0;

  const showingAllBlocks = showResponses && !accessId;
  // TODO (Peter): I didn't modify these, but the logic looks inconsistent between these two. Is that intentional?
  const notShowingCodeOutput = isCodeBlock && showingAllBlocks && !isProjecting && notFeaturing;
  const notShowingWhiteboardOutput = isWhiteboardBlock && showingAllBlocks;

  // For linked sections, check whether the instructor has set up custom seeded content.
  const hasInstructorSeed =
    isLinkedSection &&
    (block.f as FieldType[]).some((f) => instructorFieldIsInitialized(doc, f.id));

  // Instructor on linked section: compare their seeded content (d.s) against the block template.
  // Student on linked section with seeded content: compare against the seeded content.
  // All other cases: compare against the block template (existing behavior).
  const templateButtonAccessId =
    isLinkedSection && canAuthorSection ? LINKED_SECTION_SEEDING_ACCESS_ID : accessId;
  const useLinkedSeeding = hasInstructorSeed && !canAuthorSection;

  return (
    <div
      className={clsx(
        "relative",
        isAuthoringPage || isFeaturing ? "extended-page-block-width" : "page-block-width",
        `block-${block.t}`,
      )}
      // Used for block marker scrolling.  Position for div must not be static.
      id={`block-${block.id}`}
      ref={ref}
      {...blockHover.hoverParentProps}
    >
      {showAddBlock &&
        (canModifyBlocks ? (
          <AddElement doc={doc} insertAt={blockIndex} page={page} />
        ) : (
          // This is to ensure the student and instructor's have the same
          // vertical spacing between blocks, regardless of whether an add element is present.
          <VerticalSpacer useProjectorScaling={isProjecting} />
        ))}

      <div className="relative">
        <div className="absolute -left-[38px] top-0">
          {/* z-[400] to ensure the feature button is on top of the Tldraw whiteboard. */}
          {canFeature && (
            <div className="relative z-[400] mb-1">
              <FeatureButton
                accessId={accessId}
                blockId={block.id}
                blockType={block.t}
                isRollup={Boolean(showResponses)}
                pageId={pageId}
              />
            </div>
          )}

          {canModifyBlocks && (
            <span
              className={clsx(
                "body-s mb-2 mt-0 text-black-tint-40",
                !blockHover.isHovering && "visible-on-focus",
              )}
            >
              <BlockMoveButtons
                reachedBottom={isLastBlock}
                reachedTop={isFirstBlock}
                onMoveDown={() => onMove(1)}
                onMoveUp={() => onMove(-1)}
              />
            </span>
          )}
        </div>

        {canModifyBlocks && (
          <BlockToolbar
            blockType={block.t}
            className={clsx(!blockHover.isHovering && "visible-on-focus")}
            onDelete={onDelete}
          >
            {hasSwitchForMultiple && (
              <MultipleSwitchDropdown
                {...(collaborativeField as ChoiceFieldType | FileFieldType)}
                doc={doc}
                path={interactiveFieldPath}
              />
            )}

            {isCodeBlock && (
              <CodeCellTypeSwitchDropdown
                {...(collaborativeField as CodeFieldType)}
                doc={doc}
                path={interactiveFieldPath}
              />
            )}

            {isEmbedField && (
              <EditEmbedButton
                {...(collaborativeField as EmbedFieldType)}
                doc={doc}
                path={interactiveFieldPath}
              />
            )}
          </BlockToolbar>
        )}

        <div className="w-full">
          {showResponses === "ALL" && measuredField && (
            <BlockResponsesHeader
              blockType={block.t}
              doneCount={studentsMadeProgressCount}
              sticky={!isProjecting}
              titleOnly={!notFeaturing}
              totalCount={totalStudentsCount}
            />
          )}

          {block.f.map((field: FieldType, index: number) => {
            const showTemplateToolbar = index === templateFieldIndex;

            const showTemplateButton =
              templateFieldIndex !== -1 &&
              blockHasDifferingSeedText(doc, templateButtonAccessId, block.id, {
                useLinkedSeeding,
              });

            const fieldNode = (
              <Field
                accessIdPath={accessIdPath}
                blockFieldPaths={fieldPaths}
                blockHeight={blockHeight}
                blockId={block.id}
                blockType={block.t}
                doc={doc}
                fieldPath={fieldPaths[index]}
                responseAccessIds={allAccessIds}
              />
            );

            return (
              <ErrorBoundary key={field.id} minimal>
                <RenderIfVisible
                  defaultHeight={
                    ESTIMATED_AVG_FIELD_HEIGHT_PX_MAP[field.t] || FALLBACK_FIELD_HEIGHT_PX
                  }
                  initialVisible={wasFeatured || isFirstBlock}
                  stayRendered
                >
                  {showTemplateToolbar ? (
                    <HoverableToolbar
                      className="relative items-start"
                      // Extend the hoverable area to the right so users can move the mouse to toolbar buttons
                      contentClassName="!absolute -right-9 top-0 z-0 w-8"
                      extraListItems={
                        showTemplateButton ? (
                          <li>
                            <TemplateBlockButton
                              block={block}
                              doc={doc}
                              versionType={canAuthorSection ? "template" : "instructor"}
                            />
                          </li>
                        ) : undefined
                      }
                      uncentered
                    >
                      {fieldNode}
                    </HoverableToolbar>
                  ) : (
                    fieldNode
                  )}
                </RenderIfVisible>
              </ErrorBoundary>
            );
          })}
        </div>

        {notShowingCodeOutput && (
          <div className="body-s pt-4 text-black-tint-40">
            {t("fields.code.not_showing_outputs_in_class_responses")}
          </div>
        )}

        {notShowingWhiteboardOutput && (
          <div className="body-s text-black-tint-40">
            <Card className="body-s-bold mb-4 text-black-tint-20" kind="blue">
              <IconText iconStart={<WhiteboardIcon />} text={t("fields.headers.Whiteboard")} />
            </Card>

            {canAuthorSection && !isProjecting ? (
              <div className="ml-3">
                {t("fields.whiteboard.not_showing_outputs_in_class_responses_instructor")}
              </div>
            ) : (
              <div className="ml-3">
                {t("fields.whiteboard.not_showing_outputs_in_class_responses_student")}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface BlocksProps {
  accessIdPath: PathType;
  doc: Doc<WorksheetType>;
}

export const Blocks: FC<BlocksProps> = ({ doc, accessIdPath }) => {
  const dispatch = useAppDispatch();
  const docData = doc.data;
  const blocks = docData?.[BLOCKS_PATH];
  const blocksRef = useRef<HTMLDivElement>(null);
  const { canAuthorPage, isAuthoringPage, isFeaturing, showResponses, hideStaticBlocks } =
    useContext(DocViewContext);
  const isMobile = !useIsMdOrLarger();
  const { presentedBlockId } = usePresentation();
  const { pageId } = useContext(DocViewContext);
  const page = useAppSelector((state) => pageSelectors.selectById(state, pageId));
  const blockIdHash = blocks?.map((block) => block.id).join(".") ?? "";
  const blockPaths = useMemo(
    () => blocks?.map((block, blockIndex) => [BLOCKS_PATH, String(blockIndex)]) ?? [],
    // We don't properly react to changes in `blocks`, so we use an additional hash
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [blocks, blockIdHash],
  );

  useEffect(() => {
    if (Array.isArray(blocks)) {
      dispatch(
        viewStateActions.setCurrentAvailablePageBlocks(
          blocks.map((block, blockIndex) => ({
            index: blockIndex,
            blockType: block.t,
          })),
        ),
      );
      dispatch(viewStateActions.setSelectedPageBlockIndex(0));
    }
    // We don't properly react to changes in `blocks`, so we use an additional hash
  }, [blocks, blockIdHash, dispatch]);

  // When the user scrolls, we need to figure out the closest `.block-component` to
  // the top of the blocksRef element,
  useEffect(() => {
    const scrollContainer = document.getElementById(PAGE_SCROLL_CONTAINER_ID);
    if (!scrollContainer) return;

    const handleScroll = throttle(() => {
      const blockComponents = scrollContainer.querySelectorAll(".block-component");
      const arr = Array.from(blockComponents);
      const rects = arr.map((component) => component.getBoundingClientRect());
      const scrollContainerScrolledPercent =
        scrollContainer.scrollTop / scrollContainer.scrollHeight;

      if (rects.length === 0) return;

      // Naively, we'd want to find the closest block to the middle of the screen.
      // This works well for long blocks, but for short blocks, feels very buggy,
      // as it would result in the first and last blocks almost never being "closest".
      // Instead, we use a weighted approach, where the closer to the top of the page we are,
      // the more weight we give to the first block, and the closer to the bottom of the page we are,
      // the more weight we give to the last block. When the container is scrolled roughly to
      // the middle, the target Y is roughly the middle of the screen.
      // On short pages, this still almost-skips over some of the middle blocks, but it _feels_
      // consistent.
      const viewportTargetY = window.innerHeight * scrollContainerScrolledPercent * 2;

      // Find the closest block to the middle of the screen
      let closestIndex = 0;
      let minDistance = Infinity;
      rects.forEach((rect, index) => {
        // Calculate the center of the current block
        const blockCenterY = rect.top + rect.height / 2;
        // Calculate distance from block center to target y
        const distance = Math.abs(blockCenterY - viewportTargetY);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });

      dispatch(viewStateActions.setSelectedPageBlockIndex(closestIndex));
      // 50ms chosen as something that feels responsive, but not too frequent.
    }, 50);

    scrollContainer.addEventListener("scroll", handleScroll);
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [dispatch]);

  if (!page || !docData || !Array.isArray(blocks)) {
    return null;
  }

  // If an instructor is viewing a page without any blocks, always show the
  // large version of the `<AddElement />` component. We specifically do
  // *not* check for Edit Mode to be enabled (which means we have to use
  // `canAuthorPage` instead of `isAuthoringPage`), because the component
  // is smart enough to disable the "Add Block" button while Edit Mode
  // is off.
  //
  // This is similar to the `canModifyBlocks` boolean below, except that
  // `canModifyBlocks` also requires Edit Mode (via `isAuthoringPage`).
  if (blocks.length === 0 && canAuthorPage && !showResponses && !isMobile) {
    return (
      <AddElement
        className="last-element page-block-width"
        doc={doc}
        insertAt={MAX_BLOCK_COUNT}
        page={page}
        isLarge
      />
    );
  }

  if (blocks.length === 0 && !canAuthorPage) {
    return <div className="mb-7">{t("page_detail_view.worksheet_is_empty")}</div>;
  }

  const canModifyBlocks = isAuthoringPage && !showResponses && !isMobile && !isFeaturing;

  const onDelete = (blockIndex: number) => {
    deleteBlockFromWorksheet(doc, blockIndex);
    updatePageStaticContentStatus(page, doc);
    if (!hasEditableBlocks(doc)) {
      dispatch(viewStateActions.setShowGroupConfig(false));
    }
  };

  const onMove = (blockIndex: number, changeAmount: 1 | -1) => {
    if (
      (blockIndex <= 0 && changeAmount < 0) ||
      (blockIndex >= blocks.length - 1 && changeAmount > 0)
    ) {
      return toastLocalizedOperationError("cannot_move_block");
    }
    doc.submitOp({
      p: [BLOCKS_PATH, blockIndex],
      lm: blockIndex + changeAmount,
    });
  };

  const filteredBlocks =
    isFeaturing && presentedBlockId
      ? // if we're featuring a specific block, only show that block
        compact([blocks.find((block) => block.id === presentedBlockId)])
      : hideStaticBlocks
        ? // if we're showing responses, we don't show instructions-only blocks, just blocks that can have answers
          blocks.filter((block) => isQuestionBlock(block))
        : // otherwise, show all blocks
          blocks;

  return (
    <ErrorBoundary>
      <div className="flex h-full w-full flex-col items-center" ref={blocksRef}>
        {filteredBlocks.map((block, idx) => {
          const blockIndex = blocks.indexOf(block);
          const isFirstBlock = idx === 0;
          const isLastBlock = idx === filteredBlocks.length - 1;
          const fields = block?.f;
          if (!fields?.length) {
            return null;
          }

          return (
            <div
              className={clsx(
                "block-component flex w-full flex-col items-center text-black-tint-20",
                {
                  "last-element": !canModifyBlocks && isLastBlock,
                },
              )}
              id={getBlockIndexId(block.t, blockIndex)}
              key={block.id}
            >
              <Block
                accessIdPath={accessIdPath}
                blockIndex={blockIndex}
                blockPath={blockPaths[blockIndex]}
                doc={doc}
                isFirstBlock={isFirstBlock}
                isLastBlock={isLastBlock}
                key={block.id}
                onDelete={() => onDelete(blockIndex)}
                onMove={(changeAmount) => onMove(blockIndex, changeAmount)}
              />
            </div>
          );
        })}

        {canModifyBlocks && (
          <AddElement
            className="last-element page-block-width"
            doc={doc}
            insertAt={MAX_BLOCK_COUNT}
            page={page}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};
